import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { createPortal } from 'react-dom';

import { APP_ROUTES } from '@navigation/types';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useShowDataById } from '@redux/selectors/shows';
import {
  useLeftTeamSpeakerId,
  useRightTeamSpeakerId,
  useSpeakerDataById,
} from '@redux/selectors/speakers';
import { ShowStatus } from '@typings/show';
import { useAppDispatch } from '@redux/store';
import { feedSlice } from '@redux/state/feedSlice';
import { useLazyGetShowByIdQuery } from '@redux/queries/show';

import Player from './components/Player';

const LiveShowMiniPlayer = () => {
  const {
    currentShowId,
    isHost,
    onFinishOrLeaveCurrentShow,
    leaveAgoraChannel,
    resetCurrentShowState,
  } = useAgoraEngine();
  const show = useShowDataById(currentShowId);
  const leftSpeakerId = useLeftTeamSpeakerId(currentShowId);
  const rightSpeakerId = useRightTeamSpeakerId(currentShowId);
  const leftSpeakerProfileId = useSpeakerDataById(leftSpeakerId)?.profileId;
  const rightSpeakerProfileId = useSpeakerDataById(rightSpeakerId)?.profileId;
  const isFinished = useMemo(
    () => show?.status === ShowStatus.Finished,
    [show?.status],
  );
  const initialized = useRef(false);
  const [getShowById] = useLazyGetShowByIdQuery();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isLiveShowLocation = useMemo(
    () => location.pathname.includes(APP_ROUTES.SHOW_LIVE),
    [location.pathname],
  );

  const goToShowScreen = useCallback(() => {
    navigate(APP_ROUTES.SHOWS_INITIALIZATION, { state: { id: currentShowId } });
  }, [navigate, currentShowId]);

  const onClose = useCallback(() => {
    onFinishOrLeaveCurrentShow();
  }, [onFinishOrLeaveCurrentShow]);

  useEffect(() => {
    if (isFinished && !!currentShowId && !isHost && !isLiveShowLocation) {
      leaveAgoraChannel();
      resetCurrentShowState();
      dispatch(
        feedSlice.actions.removeLiveShowFromSuggestedIds({
          id: show.id,
        }),
      );
      dispatch(
        feedSlice.actions.removeLiveShowFromFollowingIds({
          id: show.id,
        }),
      );
    }
  }, [
    isFinished,
    currentShowId,
    isHost,
    leaveAgoraChannel,
    resetCurrentShowState,
    dispatch,
    show?.id,
    isLiveShowLocation,
  ]);

  useEffect(() => {
    if (currentShowId && !initialized.current && location.pathname === '/') {
      getShowById({ id: currentShowId, _ts: new Date().getTime() });
      initialized.current = true;
    }
  }, [currentShowId, getShowById, location.pathname]);

  if (!currentShowId || isLiveShowLocation) {
    return null;
  }

  return createPortal(
    <Player
      showId={currentShowId}
      leftSpeakerProfileId={leftSpeakerProfileId}
      rightSpeakerProfileId={rightSpeakerProfileId}
      onClick={goToShowScreen}
      onClose={onClose}
    />,
    document.body,
  );
};

export default memo(LiveShowMiniPlayer);
